import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@mui/material";
import { LoadingButton } from "@mui/lab"; // Import LoadingButton
import { motion, useUnmountEffect } from "framer-motion";
import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { TbMessageCircleBolt } from "react-icons/tb";
import ProjectDetails from "./ProjectDetail";
import { CiSettings } from "react-icons/ci";
import { MoreVert } from "@mui/icons-material"; // Import an icon for the menu trigger
import BackButton from "./BackButton";
import { appStore, chatState, useRoomStore } from "../zustand";
import { makeRequest } from "../util/AxiosInstance";
import { handleError } from "../util/functions";
import { Block, Notify } from "notiflix";
import { isEmpty } from "underscore";
import { Placeholder } from "react-select/animated";
import ChatEngine from "../comm/ChatEngine";
import { useRoomManager } from "../hooks/useRoomManager";
import useBodyClasses from "../hooks/useBodyClasses";
import { toast } from "sonner";
import { ChatMode } from "../types/chatmode.enum";
import { useUnreadCountForRoom } from "../hooks/useChatManager";

const TabValues = {
  "Details": 0,
  "Sharing": 1
}

function ProjectComponent() {
  const { id } = useParams();
  const location = useLocation();
  const [project, setProject] = useState({});
  const [value, setValue] = React.useState(TabValues.Details);
  const mode = appStore((state) => state.mode);
  const setRoomId = chatState((state) => state.setRoomId);
  const setRoomInfo = chatState((state) => state.setRoomInfo);
  const { getProjectRoom } = useRoomManager()
  const userRoomId = useRoomStore((state) => state.userRoomId);
  const persistedRoom = useRoomStore((state) => state.room);
  const setOpenChatBox = chatState((state) => state.setOpenChatBox);

  // State for Menu and Modal
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const openMenu = Boolean(anchorEl);

  useBodyClasses(['project-chatmode', `${value == TabValues.Sharing ? 'sharing' : 'not-sharing'}`]);

  const messageCount = useUnreadCountForRoom(id, 'project')

  useEffect(() => {
    // Check for tab=messaging in the URL
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'messaging') {
      setValue(TabValues.Sharing);
    }
  }, [location.search]);

  useEffect(() => {
    const init = async () => {
      if (!id) {
        toast.error('Project ID not found.')
        return;
      }
      try {
        let room = await getProjectRoom(id)
        if (room) {
          setRoomId(room.id)
          setRoomInfo(room)
        } else {
          Notify.failure('Could not retrieve project room information.');
        }
      } catch (error) {
        const err = handleError(error, 'Failed to initialize project room.');
        toast.error(err);
      }
    }
    init()
  }, [id, getProjectRoom, setRoomId, setRoomInfo])

  useUnmountEffect(() => {
    if (persistedRoom && userRoomId) {
      setRoomId(userRoomId)
      setRoomInfo(persistedRoom)
    }
    setOpenChatBox(false);
  })

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const a11yProps = (index) => {
    return {
      id: `full-width-tab-${index}`,
      "aria-controls": `full-width-tabpanel-${index}`,
    };
  };

  useEffect(() => {
    setOpenChatBox(true);
  }, [setOpenChatBox]);

  useEffect(() => {
    if (!id) {
      toast.error('Project ID not found.')
      return;
    }
    let isMounted = true;
    Block.circle(".project-detail");
    makeRequest("GET", `/get-project/${id}`)
      .then((response) => {
        if (isMounted) {
          setProject(response.data.data);
        }
      })
      .catch((error) => {
        if (isMounted) {
          let msg = handleError(error);
          Notify.failure(msg);
          console.error(error); // Use console.error for errors
        }
      })
      .finally(() => {
        if (isMounted) {
          Block.remove(".project-detail");
        }
      });

    return () => { // Cleanup function
      isMounted = false;
    };
  }, [id]);

  // Menu Handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Cancel Modal Handlers
  const handleCancelClick = () => {
    handleMenuClose();
    setOpenCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setOpenCancelModal(false);
    setCancelReason(''); // Reset reason on close
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason) {
      toast.warning('Please provide a reason for cancellation.');
      return;
    }
    setIsCancelling(true);
    try {
      await makeRequest("POST", `/projects/${id}/cancel/request`, {
        reason: cancelReason,
      });
      toast.success('Project cancellation request submitted successfully.');
      handleCancelModalClose();
      window.location.reload();
    } catch (error) {
      const errMsg = handleError(error, 'Failed to submit cancellation request.');
      toast.error(errMsg);
      console.error(error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleStatusClick = () => {
    handleMenuClose();
    setOpenStatusModal(true);
  };

  const handleStatusModalClose = () => {
    setOpenStatusModal(false);
    setSelectedStatus('');
  };

  const handleStatusSubmit = async () => {
    if (!selectedStatus) {
      toast.warning('Please select a status.');
      return;
    }
    setIsUpdatingStatus(true);
    try {
      await makeRequest('POST', `/projects/${id}/status`, { status: selectedStatus });
      toast.success('Project status updated.');
      handleStatusModalClose();
      window.location.reload();
    } catch (error) {
      const errMsg = handleError(error, 'Failed to update project status.');
      toast.error(errMsg);
      console.error(error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <motion.div className="project-detail">
        {!isEmpty(project) ? ( // Use ternary for cleaner conditional rendering
          <>
            <Box>
              <BackButton />
            </Box>
            <Box mt={2} mb={"2rem"} display={"flex"} alignItems={"center"}>
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                  flexGrow: 1,
                  mr: 2
                }}
              >
                <Typography
                  textTransform={"capitalize"}
                  variant="h5"
                  fontWeight={"600"}
                  noWrap // Prevent title wrapping awkwardly
                >
                  {project.title ?? "Project name"}
                </Typography>

              </Box>
              {mode === 'admin' && (
                <>
                  <IconButton
                    aria-label="manage project"
                    aria-controls={openMenu ? 'manage-project-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openMenu ? 'true' : undefined}
                    onClick={handleMenuOpen}
                    sx={{ marginLeft: "auto", flexShrink: 0 }}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    id="manage-project-menu"
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={handleMenuClose}
                    MenuListProps={{
                      'aria-labelledby': 'manage project',
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleStatusClick}>
                      Update Status
                    </MenuItem>
                    <MenuItem onClick={handleCancelClick}>
                      Cancel Project
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
            <TabContext className="single-project" value={value}>
              <Tabs
                className="tab-bar"
                value={value}
                onChange={handleChange}
                textColor="inherit"
                indicatorColor="primary"
                variant="fullWidth"
                aria-label="full width tabs example"
              >
                <Tab
                  className="tab-item"
                  icon={<IoMdInformationCircleOutline />}
                  iconPosition="start"
                  label="Details"
                  {...a11yProps(TabValues.Details)}
                />
                <Tab
                  className="tab-item"
                  icon={<TbMessageCircleBolt />}
                  iconPosition="start"
                  label={`Messages (${messageCount})`}
                  {...a11yProps(TabValues.Sharing)}
                />
              </Tabs>
              {value == TabValues.Details && <>
                <TabPanel className="project-details" value={value} index={TabValues.Details} sx={{ p: 0 }}>
                  <ProjectDetails project={project} />
                </TabPanel>
              </>}
              {value == TabValues.Sharing && <>
                <TabPanel
                  className="chat-tab"
                  value={value}
                  index={TabValues.Sharing}
                  sx={{ p: 0 }}
                >
                  <ChatEngine
                    mode={ChatMode.DM}
                    dm={{
                      id: id,
                      title: project.title
                    }} />

                </TabPanel>
              </>}
            </TabContext>
          </>
        ) : (
          <Placeholder text={"Loading project..."} />
        )}
      </motion.div>

      {/* Cancel Project Modal */}
      <Dialog open={openCancelModal} onClose={handleCancelModalClose} fullWidth maxWidth="xs">
        <DialogTitle>Cancel Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            required
            margin="dense"
            id="cancel-reason"
            label="Reason for Cancellation"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelModalClose}>Cancel</Button>
          <LoadingButton
            onClick={handleCancelSubmit}
            variant="contained"
            color="error"
            loading={isCancelling}
          >
            Confirm Cancel
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={openStatusModal} onClose={handleStatusModalClose} fullWidth maxWidth="xs">
        <DialogTitle>Update Project Status</DialogTitle>
        <DialogContent>
          <RadioGroup value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <FormControlLabel value="2" control={<Radio />} label="In Progress" />
            <FormControlLabel value="4" control={<Radio />} label="Completed" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusModalClose}>Cancel</Button>
          <LoadingButton onClick={handleStatusSubmit} loading={isUpdatingStatus} variant="contained">
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ProjectComponent;
